/**
 * **전투 준비.** 판을 시작하기 전에 한 번 묻는다: 좁은 협곡이면 데려갈 분대(`Trait.Canyon`), 군수품을 가졌으면 이번
 * 판에 쓸 것(supplies.ts), 분대가 둘 이상이면 출전 위치(Todo10, `sim/deploy.ts`). 묻을 것이 없으면 뜨지 않는다(`needsPrep`).
 *
 * 출전 위치는 여섯 칸(좌·중·우 × 앞·뒤)이다. 부대 패를 끌어 칸에 놓는다(Todo11). 처음에는 "폰에서 끌어 놓기는 스크롤과
 * 다툰다"고 누르기만 두었는데, 패에만 `touch-action: none`을 걸면 패를 잡았을 때만 스크롤이 멎는다. 누르기(패를 고른 뒤
 * 칸 이름)는 키보드와 끌 수 없는 때의 길로 남겼다.
 * 마지막 배치를 기억해(`Army.deploy`) 그대로 나서면 된다.
 *
 * 협곡은 플레이한 사람이 골랐다: "진군할 때 고른다." 출정 중에는 편성을 바꿀 수 없으므로(결과 화면은 철수·진군뿐이다)
 * 판에 들어가는 길목에서 묻는다. 출정 첫 판도 같은 자리(`openBattle`)다. 군수품도 같은 까닭으로 여기서 고른다 — 그
 * 판의 전장 특성을 보고 고르는 것이 군수품의 뜻이다(Todo5 4단계). 처음에는 협곡만 묻는 화면이었다.
 */
import { TERRITORY_BY_ID } from '../meta/realm.ts';
import { BRANCH_NAMES, COMPANIONS, TROOP_BY_ID, specForTroop } from '../sim/troops.ts';
import type { Branch } from '../sim/troops.ts';
import { branchIcon } from './branch-icon.ts';
import { dressCompanion, dressLeader } from '../sim/scenario.ts';
import { HERO, loadoutOf } from '../meta/stash.ts';
import { iconKeyFor, unitIcon } from './unit-icon.ts';
import { CANYON_SQUADS, ROUTE_BY_ID, TRAIT_BY_ID, Trait, gateStoryName } from '../sim/traits.ts';
import { SUPPLIES, SUPPLY_PER_BATTLE } from '../sim/supplies.ts';
import { supplyIcon } from './supply-icons.ts';
import type { Army } from '../meta/army.ts';
import { companionLevelOf, heroLevelOf, soldierById, supplyCount } from '../meta/army.ts';
import type { RunState } from '../meta/run.ts';
import {
  autoDeployFor,
  defaultPick,
  deployLayout,
  deployStanding,
  needsDeploy,
  needsPick,
  needsSupplyChoice,
  pickHasSoldiers,
  routeOf,
  runTraits,
  squadsAvailable,
} from '../meta/run.ts';
import { DEPLOY_SLOTS, DEPLOY_SLOT_NAMES } from '../sim/deploy.ts';
import { button, clear, el } from './dom.ts';
import { squadName } from './labels.ts';

/** 끌어 놓은 뒤 이 안에 같은 패에 오는 click은 놓기의 뒤끝이다(ms). */
const DROP_CLICK_MS = 400;

/** 제목의 셈말. "2 분대"보다 "두 분대"가 읽힌다. */
const KOREAN_COUNT: Record<number, string> = { 1: '한', 2: '두', 3: '세' };

/** 분대 한 줄 요약: "창 8 · 궁수 4". 병사가 없으면 빈 문자열. */
function makeup(army: Army, uids: readonly number[]): string {
  const count = new Map<string, number>();
  const order: string[] = [];
  for (const uid of uids) {
    const s = soldierById(army, uid);
    const t = s ? TROOP_BY_ID.get(s.troop) : undefined;
    if (!t) continue;
    const name = BRANCH_NAMES[t.branch];
    if (!count.has(name)) order.push(name);
    count.set(name, (count.get(name) ?? 0) + 1);
  }
  return order.map((n) => `${n} ${count.get(n)}`).join(' · ');
}

/** 부대 패에 싣는 병력 구성의 한 가지: 가지, 그 이름(읽어 주는 말), 수. */
export interface MakeupPart {
  branch: Branch;
  name: string;
  count: number;
}

/**
 * 부대 패에 싣는 병력 구성: 가지마다 하나, 많은 가지부터(같으면 명부에 먼저 나온 쪽). 출전 위치를 정할 때 그 부대가
 * 무엇으로 이루어졌는지 보여야 한다 — 창은 앞에, 궁수는 뒤에 세운다(플레이한 사람이 청했다: "부대 병력구성도 요약해서
 * 보여줘"). 패에는 부대 이름과 부대장만 있어, 구성을 보려면 위의 편성 줄로 눈을 올려야 했다. 패는 가지를 기호 그림으로
 * 그린다(`branchIcon`).
 */
export function makeupParts(army: Army, uids: readonly number[]): MakeupPart[] {
  const count = new Map<Branch, number>();
  for (const uid of uids) {
    const s = soldierById(army, uid);
    const t = s ? TROOP_BY_ID.get(s.troop) : undefined;
    if (!t) continue;
    count.set(t.branch, (count.get(t.branch) ?? 0) + 1);
  }
  // Map은 넣은 순서를 지킨다 — 안정 정렬이라 같은 수면 명부에 먼저 나온 가지가 앞선다.
  return [...count].map(([branch, n]) => ({ branch, name: BRANCH_NAMES[branch], count: n })).sort((a, b) => b.count - a.count);
}

export interface PrepChoice {
  /** 데려갈 분대(좁은 협곡). 묻지 않았으면 null. */
  readonly squads: number[] | null;
  /** 이번 판에 쓸 군수품. 묻지 않았으면 null. */
  readonly supplies: string[] | null;
  /** 분대별 출전 위치(칸 번호, 서지 않는 분대는 -1). 묻지 않았으면 null. */
  readonly deploy: number[] | null;
}

export function renderBattlePrep(root: HTMLElement, army: Army, run: RunState, onDone: (c: PrepChoice) => void): void {
  const askSquads = needsPick(run);
  const askSupplies = needsSupplyChoice(army, run);
  const askDeploy = needsDeploy(run);
  const traits = runTraits(run, run.depth);
  const land = run.territory ? TERRITORY_BY_ID.get(run.territory.id) : undefined;
  const way = ROUTE_BY_ID.get(routeOf(run, run.depth) ?? '');
  const canyon = TRAIT_BY_ID.get(Trait.Canyon)!;

  // 분대: 병사가 있는 분대부터 골라 둔다. 그대로 나가도 되는 기본값이다(`defaultPick`).
  const options = askSquads ? squadsAvailable(run) : [];
  let picked = askSquads ? defaultPick(run) : [];
  // 군수품: 처음에는 아무것도 쓰지 않는다 — 쓰면 사라지는 것이라 묻지 않고 쓰면 안 된다.
  let using: string[] = [];
  // 출전 위치: 마지막 배치(없으면 기본). 협곡에서 데려갈 분대를 바꾸면 서는 분대가 바뀌므로 그릴 때마다 다시 거른다.
  let slots = askDeploy ? deployLayout(army, run, askSquads ? picked : undefined) : [];
  let moving = -1;
  // 누른 단추. 다시 그린 뒤 그 단추에 초점을 돌린다 — 목록을 통째로 다시 그려 누른 단추가 사라지면 키보드 초점이 길을
  // 잃었다(리뷰가 짚었다).
  let focusKey = '';

  const squadList = el('div', { class: 'pick-list', role: 'group', 'aria-label': '데려갈 분대' });
  const supplyList = el('div', { class: 'pick-list', role: 'group', 'aria-label': '이번 판에 쓸 군수품' });
  const deployBoard = el('div', { class: 'deploy-board', role: 'group', 'aria-label': '출전 위치 — 위가 적 쪽' });
  /**
   * 패의 병력 구성 줄 맞추기. 구성은 좁은 패에서 두 줄로 접힌다(폰에서 세 병종이면) — 한 패만 접히면 그 패만 키가 커져
   * 줄이 들쭉날쭉했다(플레이한 사람: "너무 좁으면 모두를 두 줄 공간 확보해서 높이를 동일하게 맞추자"). 어느 패든 접히면
   * 모든 패가 가장 많이 접힌 패만큼의 자리를 갖는다(`rows2`, `--men-h`). 접히는지는 폭이 정하므로 그린 뒤에 잰다 — 병종
   * 수로 어림하면 넓은 화면에서 빈 줄이 생긴다. 판의 폭이 바뀌면(화면을 돌리면) 다시 잰다.
   *
   * 자리는 가장 큰 구성의 **잰 높이**다. 처음에는 두 줄 높이로 못 박았는데, 폭 360에서 다섯 병종은 세 줄이 되어 그
   * 패만 16px 더 컸다(QA가 쟀다).
   */
  const fitMenRows = (): void => {
    deployBoard.classList.remove('rows2');
    let wrapped = false;
    let tallest = 0;
    for (const men of deployBoard.querySelectorAll<HTMLElement>('.deploy-men')) {
      const kinds = men.querySelectorAll<HTMLElement>('.deploy-kind');
      // 첫 덩어리와 끝 덩어리의 높이가 다르면 줄이 바뀐 것이다.
      if (kinds.length > 1 && kinds[kinds.length - 1].offsetTop > kinds[0].offsetTop + 2) wrapped = true;
      tallest = Math.max(tallest, men.offsetHeight);
    }
    if (wrapped) deployBoard.style.setProperty('--men-h', `${tallest}px`);
    deployBoard.classList.toggle('rows2', wrapped);
  };
  if (typeof ResizeObserver !== 'undefined') {
    let lastWidth = -1;
    new ResizeObserver(() => {
      // 폭이 바뀔 때만 — 줄 자리를 넣고 빼면 높이가 바뀌어 다시 불린다.
      const w = deployBoard.clientWidth;
      if (w === lastWidth) return;
      lastWidth = w;
      fitMenRows();
    }).observe(deployBoard);
  }
  const count = el('small', { 'aria-live': 'polite' });
  const go = button(
    '',
    () => onDone({ squads: askSquads ? picked : null, supplies: askSupplies ? using : null, deploy: askDeploy ? slots : null }),
    'danger',
  );
  go.append('나선다', count);

  /** 부대장의 이름(장수 분대는 '장수', 동료가 없으면 null). */
  const leadName = (sq: number): string | null => {
    const mate = run.companions[sq];
    return mate ? (COMPANIONS.find((c) => c.id === mate)?.name ?? mate) : sq === 0 ? '장수' : null;
  };
  /**
   * 부대장의 그림 열쇠(착장을 입힌 몸 — 출정 화면의 카드와 같은 그림). 지휘관이 없는 분대는 첫 병사다.
   * 플레이한 사람이 청했다(Todo11): "각 부대장 캐릭터도 그려주고."
   */
  const leadKey = (sq: number): string | null => {
    if (sq === 0) return iconKeyFor(dressLeader(army.culture, loadoutOf(army, HERO), heroLevelOf(army)));
    const c = COMPANIONS.find((m) => m.id === run.companions[sq]);
    if (c) return iconKeyFor(dressCompanion(c, companionLevelOf(army, c.id), loadoutOf(army, c.id), army.culture));
    const first = soldierById(army, (run.squads[sq] ?? [])[0] ?? -1);
    return first && TROOP_BY_ID.has(first.troop) ? iconKeyFor(specForTroop(first.troop)) : null;
  };
  const moveTo = (sq: number, slot: number, focus: string): void => {
    slots = slots.map((s, k) => (k === sq ? slot : s));
    moving = -1;
    focusKey = focus;
    draw();
  };
  /**
   * 방금 끌어다 놓은 패와 그 시각 — 놓은 뒤 **그 패에** 곧바로 오는 click을 삼킨다(끌기는 고르기가 아니다). 처음에는
   * 참·거짓 하나로 두고 다음 틱에 풀었다. 터치에서는 click이 그보다 늦게 오기도 하고, 그 사이 다른 패를 누르면 그
   * 누름이 삼켜졌다(QA: 끌어 놓은 직후 다른 패를 눌렀는데 11번에 2번 안 골라졌다).
   */
  let dropped: { sq: number; at: number } | null = null;
  /**
   * 지금 끄는 중이면 그것을 끝내는 함수. 그림자는 화면에 하나뿐이라 화면이 쥔다 — 처음에는 패마다 따로 쥐어, 끄는 중에
   * 다른 입력(키보드로 다른 패를 옮김, 협곡·군수품 고르기)이 판을 다시 그리면 잡고 있던 패가 사라져 `pointerup`이 오지
   * 않고, body에 붙인 그림자가 다음 화면까지 남았다(리뷰가 재현했다). 다시 그리기 전에 끝내고(`draw`), 그림자는 배치판
   * 안에 둔다 — 판을 비우거나 화면을 떠나면 함께 사라진다.
   */
  let endDrag: (() => void) | null = null;
  /**
   * 패를 끌어 칸에 놓는다(Todo11: "부대 위치 지정하는 ui, 드래그로 할 수 있게 해줘"). 포인터 이벤트라 손가락·마우스가 같은
   * 길이다. 8px 넘게 움직여야 끌기다 — 그 안이면 누른 것(고르기)이다. 끄는 동안 패의 그림자가 손가락을 따라오고, 아래 칸에
   * 테두리가 선다. 칸 밖에 놓으면 제자리다.
   */
  const dragToken = (token: HTMLButtonElement, sq: number): void => {
    let startX = 0;
    let startY = 0;
    let ghost: HTMLElement | null = null;
    let over: HTMLElement | null = null;
    const cellAt = (x: number, y: number): HTMLElement | null =>
      (document.elementFromPoint(x, y) as HTMLElement | null)?.closest<HTMLElement>('.deploy-cell') ?? null;
    const end = (): void => {
      ghost?.remove();
      ghost = null;
      over?.classList.remove('over');
      over = null;
      token.classList.remove('dragging');
      endDrag = null;
    };
    token.addEventListener('pointerdown', (ev) => {
      if (ev.button !== 0) return;
      startX = ev.clientX;
      startY = ev.clientY;
      token.setPointerCapture(ev.pointerId);
    });
    token.addEventListener('pointermove', (ev) => {
      if (!token.hasPointerCapture(ev.pointerId)) return;
      if (!ghost) {
        if (Math.abs(ev.clientX - startX) + Math.abs(ev.clientY - startY) < 8) return;
        ghost = token.cloneNode(true) as HTMLElement;
        ghost.classList.add('deploy-ghost');
        ghost.removeAttribute('aria-label');
        ghost.setAttribute('aria-hidden', 'true');
        // 복제한 단추가 Tab 순서에 들지 않게.
        ghost.tabIndex = -1;
        ghost.style.width = `${token.offsetWidth}px`;
        deployBoard.append(ghost);
        token.classList.add('dragging');
        endDrag = end;
      }
      ghost.style.left = `${ev.clientX}px`;
      ghost.style.top = `${ev.clientY}px`;
      const cell = cellAt(ev.clientX, ev.clientY);
      if (cell !== over) {
        over?.classList.remove('over');
        cell?.classList.add('over');
        over = cell;
      }
    });
    token.addEventListener('pointerup', (ev) => {
      if (!ghost) return;
      const cell = cellAt(ev.clientX, ev.clientY);
      end();
      dropped = { sq, at: performance.now() };
      const slot = cell ? Number(cell.dataset.slot) : -1;
      if (slot >= 0 && slot !== slots[sq]) moveTo(sq, slot, `dc${sq}`);
    });
    token.addEventListener('pointercancel', end);
    token.addEventListener('lostpointercapture', end);
  };

  const draw = (): void => {
    endDrag?.();
    let focusCard: HTMLButtonElement | null = null;
    clear(squadList);
    for (const sq of options) {
      const uids = run.squads[sq] ?? [];
      const mate = run.companions[sq];
      const lead = mate ? (COMPANIONS.find((c) => c.id === mate)?.name ?? mate) : sq === 0 ? '장수' : '지휘관 없음';
      const on = picked.includes(sq);
      const card = button('', () => {
        // 이미 둘을 골랐으면 먼저 고른 쪽을 내려놓고 새 것을 든다 — 해제부터 하라고 막으면 폰에서 두 번 눌러야 한다.
        picked = on ? picked.filter((p) => p !== sq) : [...picked, sq].slice(-CANYON_SQUADS);
        focusKey = `sq${sq}`;
        draw();
      }, `pick-card${on ? ' on' : ''}`);
      if (focusKey === `sq${sq}`) focusCard = card;
      card.setAttribute('aria-pressed', String(on));
      card.append(
        el('b', {}, squadName(sq)),
        el('span', { class: 'pick-lead' }, lead),
        el('span', { class: 'pick-men' }, uids.length > 0 ? `${uids.length}명 · ${makeup(army, uids)}` : '병사 없음'),
      );
      squadList.append(card);
    }
    clear(supplyList);
    if (askSupplies) {
      for (const s of SUPPLIES) {
        const have = supplyCount(army, s.id);
        if (have <= 0) continue;
        const on = using.includes(s.id);
        const card = button('', () => {
          using = on ? using.filter((u) => u !== s.id) : [...using, s.id].slice(-SUPPLY_PER_BATTLE);
          focusKey = `su${s.id}`;
          draw();
        }, `pick-card${on ? ' on' : ''}`);
        if (focusKey === `su${s.id}`) focusCard = card;
        card.setAttribute('aria-pressed', String(on));
        card.classList.add('with-icon');
        card.append(
          supplyIcon(s.id, 40),
          el('b', {}, `${s.name} ${s.hanja}`),
          el('span', { class: 'pick-men' }, s.summary),
          el('span', { class: 'pick-lead' }, `가진 것 ${have} · 쓰면 하나 준다`),
        );
        supplyList.append(card);
      }
    }
    if (askDeploy) {
      const standing = deployStanding(run, askSquads ? picked : undefined);
      // 서는 분대가 바뀌었으면(협곡 고르기) 새로 선 분대는 기본 칸, 빠진 분대는 -1.
      const auto = autoDeployFor(army, run, standing);
      slots = auto.map((a, sq) => (a < 0 ? -1 : slots[sq] >= 0 ? slots[sq] : a));
      if (!standing.includes(moving)) moving = -1;
      const label = (sq: number): string => {
        const lead = leadName(sq);
        return lead ? `${squadName(sq)} · ${lead}` : squadName(sq);
      };
      clear(deployBoard);
      for (let slot = 0; slot < DEPLOY_SLOTS; slot++) {
        const here = standing.filter((sq) => slots[sq] === slot);
        const cell = el('div', { class: `deploy-cell${here.includes(moving) ? ' on' : ''}`, 'data-slot': String(slot) });
        const place = (): void => {
          if (moving < 0) return;
          moveTo(moving, slot, `ds${slot}`);
        };
        // 칸 이름이 곧 "여기에 세우기" 단추다(누르거나 키보드로). 끌어다 놓는 것이 기본이고, 이것은 끌 수 없을 때의 길이다.
        const name = button(DEPLOY_SLOT_NAMES[slot], place, 'deploy-here');
        const others = here.filter((sq) => sq !== moving).map(squadName);
        // 옮기는 분대가 이미 이 칸에 있으면 그렇다고 읽는다 — 처음에는 "좌익 뒤 — 제2부대에 제2부대 세우기"로 같은 이름을
        // 두 번 읽었다(QA).
        name.setAttribute(
          'aria-label',
          moving < 0
            ? `${DEPLOY_SLOT_NAMES[slot]}${others.length > 0 ? `(${others.join(', ')})` : ''}`
            : here.includes(moving)
              ? `${DEPLOY_SLOT_NAMES[slot]} — ${squadName(moving)}가 서 있다${others.length > 0 ? `(함께: ${others.join(', ')})` : ''}`
              : `${DEPLOY_SLOT_NAMES[slot]}${others.length > 0 ? `(${others.join(', ')})` : ''}에 ${squadName(moving)} 세우기`,
        );
        if (focusKey === `ds${slot}`) focusCard = name;
        // 칸의 빈 자리를 눌러도 세운다(폰에서 작은 이름 단추를 맞추기 어렵다). 패(단추)를 누른 것은 패가 받는다.
        cell.addEventListener('click', (ev) => {
          if (ev.target === cell) place();
        });
        cell.append(name);
        for (const sq of here) {
          const on = sq === moving;
          const uids = run.squads[sq] ?? [];
          const token = button('', () => {
            // 끌어다 놓은 바로 뒤 그 패에 오는 click은 고르기가 아니다(`dropped`).
            const just = dropped;
            dropped = null;
            if (just && just.sq === sq && performance.now() - just.at < DROP_CLICK_MS) return;
            moving = on ? -1 : sq;
            focusKey = `dc${sq}`;
            draw();
          }, `deploy-token${on ? ' on' : ''}`);
          token.setAttribute('aria-pressed', String(on));
          token.setAttribute(
            'aria-label',
            `${label(sq)} — ${uids.length > 0 ? makeup(army, uids) : '부하장수 혼자'} · ${DEPLOY_SLOT_NAMES[slot]}에 서 있다. 끌어서 옮기거나, 고른 뒤 칸 이름을 누른다`,
          );
          token.append(
            // 전신으로 그린다. 처음에는 가슴 위(24px)만 잘라 넣었는데, 말 탄 장수는 머리와 말 목만 걸려 누구인지
            // 알아볼 수 없었다(플레이한 사람이 짚었다: "아이콘에 캐릭터 전신이 보이게 고쳐줘").
            unitIcon(leadKey(sq), 'whole'),
            el('span', { class: 'deploy-who' }, el('b', {}, squadName(sq)), el('small', {}, leadName(sq) ?? '')),
            // 병력 구성. 가지마다 기호 그림과 수가 한 덩어리라 좁은 칸에서는 덩어리째 줄을 바꾼다.
            el(
              'span',
              { class: 'deploy-men' },
              ...(uids.length > 0
                ? makeupParts(army, uids).map((p) =>
                    // 그림은 꾸밈이라(`aria-hidden`) 읽어 주는 말은 패의 이름표(`aria-label`)가 맡는다. 눈으로 보는
                    // 사람에게는 풍선말(`title`)로 가지 이름을 단다.
                    el('span', { class: 'deploy-kind', title: `${p.name} ${p.count}` }, branchIcon(p.branch, 16), String(p.count)),
                  )
                : [el('span', { class: 'none' }, leadName(sq) ? '병사 없음' : '')]),
            ),
          );
          dragToken(token, sq);
          if (focusKey === `dc${sq}`) focusCard = token;
          cell.append(token);
        }
        deployBoard.append(cell);
      }
      fitMenRows();
    }
    // 병사가 하나도 없는 분대 고르기는 나갈 수 없다(`pickHasSoldiers`) — 동료만으로는 판이 첫 틱에 끝난다.
    const manned = !askSquads || pickHasSoldiers(run, picked);
    const parts: string[] = [];
    if (askSquads) parts.push(picked.length === 0 ? '분대를 고르라' : manned ? `분대 ${picked.length}/${CANYON_SQUADS}` : '병사가 있는 분대를 하나는 넣어라');
    if (askSupplies) parts.push(using.length > 0 ? `군수품 ${using.length}` : '군수품 없이');
    count.textContent = parts.join(' · ');
    go.disabled = askSquads && (picked.length === 0 || !manned);
    focusCard?.focus();
  };
  function resetDeploy(): void {
    slots = autoDeployFor(army, run, deployStanding(run, askSquads ? picked : undefined));
    focusKey = '';
    draw();
  }
  draw();

  // 이 판의 전장 특성. 군수품은 그것에 맞춰 고른다.
  const traitLines = traits.map((t) => TRAIT_BY_ID.get(t)).filter((t) => t !== undefined);
  clear(root);
  root.append(
    el(
      'div',
      { class: 'result-wrap' },
      el(
        'header',
        { class: 'result-head' },
        el(
          'span',
          { class: 'eyebrow' },
          // 영토 공격(Todo9)은 깊이가 아니라 그 땅이다 — "깊이 13"이라 적으면 출정의 판으로 읽혔다(리뷰가 짚었다).
          land
            ? `영토전 · ${land.name} · ${askSquads ? `${canyon.name} ${canyon.hanja}` : '전투 준비'}`
            : askSquads
              ? `깊이 ${run.depth} · ${canyon.name} ${canyon.hanja}`
              : `깊이 ${run.depth} · ${gateStoryName(run.depth) ? `관문 — ${gateStoryName(run.depth)}` : way ? way.name : '전투 준비'}`,
        ),
        el(
          'h1',
          {},
          askSquads ? `${KOREAN_COUNT[CANYON_SQUADS] ?? CANYON_SQUADS} 분대만 들어간다` : askSupplies ? '무엇을 쓰고 나설까' : '어디에 세우고 나설까',
        ),
      ),
      traitLines.length > 0
        ? el(
            'section',
            { class: 'panel pick-note' },
            ...traitLines.map((t) => el('p', {}, el('b', {}, t.name), ` — ${t.summary}`)),
            ...traitLines.map((t) => el('p', { class: 'dim' }, t.hint)),
            askSquads
              ? el('p', { class: 'dim' }, land ? '남은 분대는 이 싸움에 들지 않고 그대로 돌아온다.' : '남은 분대는 이 판만 쉬고, 다음 깊이에서 다시 싸운다.')
              : null,
          )
        : null,
      askSquads ? squadList : null,
      // 출전 위치를 군수품 위에 둔다 — 군수품을 많이 가지면 폰에서 배치판이 한참 아래로 밀려 못 보고 나섰다(QA).
      askDeploy
        ? el(
            'section',
            { class: 'prep-sec' },
            el('h2', { class: 'prep-h' }, '출전 위치', el('small', {}, ' 부대를 끌어 칸에 놓는다(누른 뒤 칸 이름을 눌러도 된다) · 위가 적 쪽')),
            deployBoard,
            el('div', { class: 'deploy-foot' }, button('기본 배치로', () => resetDeploy(), 'mini')),
          )
        : null,
      askSupplies
        ? el(
            'section',
            { class: 'prep-sec' },
            el('h2', { class: 'prep-h' }, '군수품', el('small', {}, ` 이번 판에 쓸 것 — ${KOREAN_COUNT[SUPPLY_PER_BATTLE] ?? SUPPLY_PER_BATTLE} 가지까지`)),
            supplyList,
          )
        : null,
      el('div', { class: 'result-actions' }, go),
    ),
  );
}
